import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { BackButton, Button, FloatPill, Handle, MapSheet, Sheet, SheetFoot, VehicleRow, sheetText, useNote } from '../components/ui';
import { Icon } from '../components/Icon';
import { MapLine, MapView } from '../components/MapView';
import { Screen } from '../components/Screen';
import { useApp } from '../lib/app';
import { friendly } from '../lib/errors';
import { clock, km, minutes, rupees } from '../lib/format';
import { dropAt, useNow } from '../lib/eta';
import { samePlace } from '../lib/geo';
import { routeLine } from '../lib/polyline';
import { useLayout } from '../lib/layout';
import { useBack, useEscapeBack } from '../lib/nav';
import { EmptyArt } from '../art/Empty';
import { useT } from '../lib/i18n';
import { shortName } from '../lib/places';
import { getQuote, isFresh } from '../lib/quotes';
import { rewardOff } from '../lib/rewards';
import { decodePlace, tripParams } from '../lib/tripUrl';
import type { EarnedReward, Nearby, Place, Quote, QuoteOption } from '../lib/types';
import { LIST_ORDER, meta, sortBy, words } from '../lib/vehicles';
import { useColours } from '../theme/usePalette';
import { DropSquare, MapPill, MapStrip, StartRing } from './mapBits';

/** The trip at the top of the panel on tablets and desktops (Uber web): pickup and drop, each one tap to change. */
function TripSummary({ pickup, drop, pickupIsHere }: { pickup: Place; drop: Place; pickupIsHere?: boolean }) {
  const t = useT();
  const nav = useNavigate();
  const row = (kind: 'pickup' | 'drop', label: string) => (
    <button type="button" className="zr-row zr-press-soft" aria-label={kind === 'pickup' ? t('choose.editPickup', { place: label }) : t('choose.editDrop', { place: label })} onClick={() => nav({ to: '/search', search: { field: kind === 'pickup' ? 'pickup' : undefined } })} style={{ minHeight: 52, padding: '0 14px', gap: 14 }}>
      <span aria-hidden="true" style={kind === 'pickup' ? { flex: 'none', width: 12, height: 12, borderRadius: 6, boxSizing: 'border-box', border: '3px solid var(--acText)' } : { flex: 'none', width: 12, height: 12, borderRadius: 3, background: 'var(--ink)' }} />
      <span className="zr-ellipsis" style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700 }}>{label}</span>
      <Icon name="chevronRight" size={18} style={{ flex: 'none', color: 'var(--ink3)' }} />
    </button>
  );
  return (
    <div className="zr-group" role="group" aria-label={t('choose.trip')} style={{ marginTop: 16, flex: 'none' }}>
      {row('pickup', pickupIsHere ? t('loc.current') : shortName(pickup.address))}
      {row('drop', shortName(drop.address))}
    </div>
  );
}

export function ChooseRide() {
  const { api, draft, setDraft, settings } = useApp();
  const t = useT();
  const nav = useNavigate();
  const back = useBack('/search');
  useEscapeBack(back);
  const L = useLayout();
  const pal = useColours('map');
  const now = useNow();
  const url = useSearch({ from: '/choose' });
  const [refreshNote, say] = useNote();

  // the address is the trip: a refresh, Back/Forward or a pasted link opens this trip even with an empty draft
  const urlFrom = decodePlace(url.from);
  const urlTo = decodePlace(url.to);
  useEffect(() => {
    const patch: Parameters<typeof setDraft>[0] = {};
    if (urlTo && !samePlace(urlTo, draft.drop, 5)) Object.assign(patch, { drop: { ...urlTo, address: urlTo.address ?? draft.drop?.address ?? null }, quote: null });
    if (urlFrom && !samePlace(urlFrom, draft.pickup, 5)) Object.assign(patch, { pickup: { ...urlFrom, address: urlFrom.address ?? draft.pickup.address }, pickupIsHere: false, quote: null });
    if (Object.keys(patch).length) setDraft(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url.from, url.to]);
  // …and the draft is written back into the address (replace: no new history entry). On the first render an
  // address that already names a trip wins (the effect above is copying it into the draft).
  const first = useRef(true);
  useEffect(() => {
    const wasFirst = first.current;
    first.current = false;
    if (wasFirst && urlTo) return;
    if (!draft.drop || (urlTo && samePlace(urlTo, draft.drop, 5) && urlFrom && samePlace(urlFrom, draft.pickup, 5))) return;
    void nav({ to: '/choose', search: tripParams(draft.pickup, draft.drop), replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.drop, draft.pickup]);

  const drop = useMemo(() => draft.drop ?? decodePlace(url.to), [draft.drop, url.to]);
  const pickup = draft.pickup;
  const matches = (q: Quote | null) => !!q && isFresh(q) && samePlace(q.pickup, pickup) && !!drop && samePlace(q.drop, drop);
  const [quote, setQuote] = useState<Quote | null>(matches(draft.quote) ? draft.quote : null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [nearby, setNearby] = useState<Nearby | null>(null);
  const [rewards, setRewards] = useState<EarnedReward[]>([]);
  const [payOpen, setPayOpen] = useState(false);
  const [refreshed, setRefreshed] = useState(false);

  useEffect(() => {
    if (!drop && !urlTo) nav({ to: '/search', replace: true });
  }, [drop, urlTo, nav]);

  // a quote for another trip (pickup or drop changed) is not this trip's price
  useEffect(() => {
    if (quote && !matches(quote)) setQuote(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup.lat, pickup.lng, drop?.lat, drop?.lng]);

  useEffect(() => {
    if (!drop || quote) return;
    let live = true;
    getQuote(api, pickup, drop).then(
      (q) => {
        if (!live) return;
        setError(null);
        setQuote(q);
        setDraft({ quote: q });
      },
      (e) => live && setError(friendly(e, t)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, pickup.lat, pickup.lng, drop?.lat, drop?.lng, quote, attempt, t]);

  useEffect(() => {
    let live = true;
    const load = () => api.nearby(pickup).then((n) => live && setNearby(n), () => undefined);
    void load();
    // drivers move: pickup estimates (and so drop-off times) stay fresh while the rider decides
    const tm = window.setInterval(load, 20_000);
    api.rewards().then((r) => live && setRewards(r.available), () => undefined);
    return () => {
      live = false;
      window.clearInterval(tm);
    };
  }, [api, pickup]);

  // a quote lasts a few minutes: get a fresh one quietly before it runs out (and say so once the price is back)
  useEffect(() => {
    if (!quote || !drop) return;
    const ms = Math.max(5_000, Date.parse(quote.expiresAt) - Date.now() - 50_000);
    const tm = window.setTimeout(() => {
      setRefreshed(true);
      setQuote(null);
    }, ms);
    return () => window.clearTimeout(tm);
  }, [quote, drop]);
  useEffect(() => {
    if (quote && refreshed) {
      say(t('choose.refreshed'), 'good');
      setRefreshed(false);
    }
  }, [quote, refreshed, say, t]);

  const options: QuoteOption[] = useMemo(() => (quote ? sortBy(quote.options, LIST_ORDER) : []), [quote]);
  const selectedType = options.some((o) => o.vehicleType === draft.vehicleType) ? draft.vehicleType : (options[0]?.vehicleType ?? draft.vehicleType);
  const selected = options.find((o) => o.vehicleType === selectedType) ?? null;
  const etas = Object.fromEntries((nearby?.types ?? []).map((x) => [x.vehicleType, x.etaMinutes])) as Record<string, number>;
  const fastest = Object.entries(etas).sort((a, b) => a[1] - b[1])[0]?.[0];
  const reward = rewards.find((r) => r.id === draft.rewardId) ?? null;
  const off = reward && selected ? rewardOff(reward, selected.farePaise) : 0;
  const noneNear = !!nearby && !!selected && etas[selected.vehicleType] == null;
  const dropTime = (o: QuoteOption) => (quote ? dropAt(now, etas[o.vehicleType], quote.durationSeconds) : null);

  // "2 min · Drop 8:42 pm" — the driver's pickup estimate and when the rider would get there
  const sub = (o: QuoteOption, isSel: boolean) => {
    const e = etas[o.vehicleType];
    if (e == null) return nearby ? t('choose.noneNow') : '…';
    const d = dropTime(o);
    // the selected row says "away" only when there is no drop time to fit beside it
    const when = isSel && !d ? t('choose.away', { n: e }) : t('choose.min', { n: e });
    return `${when}${d ? ` · ${t('choose.drop', { time: clock(d) })}` : ''}${!isSel && fastest === o.vehicleType && options.length > 1 ? ` · ${t('choose.fastest')}` : ''}`;
  };

  // the road line from the quote (Ola); the drawn curve when the server has none
  const poly = quote?.routePolyline ?? null;
  const route = useMemo(() => (drop ? routeLine(pickup, drop, poly) : []), [pickup, drop, poly]);
  if (!drop) return null;
  const selDrop = selected ? dropTime(selected) : null;

  return (
    <Screen name="choose-ride" label={t('choose.title')}>
      <MapView
        label={t('choose.route', { from: shortName(pickup.address), to: shortName(drop.address) })}
        center={pickup}
        zoom={12}
        fit={{ points: [pickup, drop], padding: L.wide ? { top: 110, bottom: 90, left: 150, right: 190 } : { top: 150, bottom: 56, left: 124, right: 40 }, maxZoom: 15 }}
      >
        <MapLine id="route" coords={route} color={pal.route} width={4.5} casing={pal.routeCase} casingWidth={9} />
        <StartRing at={pickup} />
        <DropSquare at={drop} />
        <MapPill at={pickup} side="left">{draft.pickupIsHere ? t('loc.current') : shortName(pickup.address)}</MapPill>
        <MapPill at={drop} side="left">
          {shortName(drop.address)}
          {selDrop && <span key={clock(selDrop)} className="zr-tick" style={{ fontWeight: 500, color: 'var(--ink2)' }}>{clock(selDrop)}</span>}
        </MapPill>
      </MapView>

      <BackButton floating onClick={back} style={{ position: 'absolute', left: 'calc(var(--map-l) + 20px)', top: 'calc(var(--sat) + 10px)', zIndex: 5 }} />
      {quote && (
        <FloatPill style={{ position: 'absolute', right: 20, top: 'calc(var(--sat) + 14px)', zIndex: 5, animation: 'zr-fade .26s var(--ease) both' }}>
          {km(quote.distanceMeters)} <span style={{ color: 'var(--ink3)' }}>·</span> {minutes(quote.durationSeconds)} {t('common.min')}
        </FloatPill>
      )}

      <MapSheet label={t('choose.title')} art={<MapStrip />} style={{ minHeight: 'min(500px, 60dvh)' }}>
        <Handle />
        {L.wide && <TripSummary pickup={pickup} drop={drop} pickupIsHere={draft.pickupIsHere} />}
        <div style={{ marginTop: 22, minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flex: 'none' }}>
          <h2 style={{ margin: 0, fontSize: 20, lineHeight: '24px', fontWeight: 700, letterSpacing: '-0.015em' }}>{t('choose.title')}</h2>
          <span style={{ height: 28, padding: '0 10px', borderRadius: 14, background: 'var(--acSoft)', color: 'var(--acText)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
            <Icon name="shield2" size={14} sw={2.4} />
            {t('choose.fixed')}
          </span>
        </div>

        <div className="zr-sheet-body" role="radiogroup" aria-label={t('choose.title')} style={{ marginTop: 14, display: 'flex', flexDirection: 'column', flex: '0 1 auto', minHeight: 0 }}>
          {!quote && !error && (
            // skeleton rows of the real rows' heights, so nothing moves when the fares land
            <div aria-busy="true" aria-label={t('choose.getting')} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[84, 58, 58].map((h, i) => (
                <span key={i} aria-hidden="true" className="zr-skeleton" style={{ height: h, borderRadius: 18, animationDelay: `${i * 0.12}s` }} />
              ))}
            </div>
          )}
          {error && (
            <div role="alert" style={{ padding: '6px 0 4px' }}>
              <p style={{ ...sheetText, margin: 0 }}>{error}</p>
              <Button
                variant="secondary"
                style={{ marginTop: 12 }}
                onClick={() => {
                  setError(null);
                  setAttempt((n) => n + 1);
                }}
              >
                {t('common.retry')}
              </Button>
            </div>
          )}
          <div className="zr-stagger" style={{ display: 'flex', flexDirection: 'column' }}>
            {options.map((o, i) => {
              const w = words(t, o.vehicleType);
              const isSel = o.vehicleType === selectedType;
              return (
                <VehicleRow
                  key={o.vehicleType}
                  type={o.vehicleType}
                  name={w.label}
                  seats={meta(o.vehicleType).seats}
                  seatsLabel={t.n('choose.seatsAria', meta(o.vehicleType).seats)}
                  sub={sub(o, isSel)}
                  fare={rupees(o.farePaise)}
                  selected={isSel}
                  onSelect={() => setDraft({ vehicleType: o.vehicleType })}
                  label={`${w.label}, ${rupees(o.farePaise)}`}
                  style={{ marginTop: i && (isSel || options[i - 1].vehicleType === selectedType) ? 6 : 0 }}
                />
              );
            })}
          </div>
          {noneNear && selected && (
            <div data-testid="none-near" className="zr-fadein" style={{ margin: '8px 2px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <EmptyArt kind="drivers" width={72} style={{ flex: 'none' }} />
              <p style={{ ...sheetText, margin: 0, fontSize: 13, lineHeight: '18px' }}>
                {t('choose.noDrivers', { plural: words(t, selected.vehicleType).plural, mins: Math.round((settings?.matchingTimeoutSeconds ?? 180) / 60) })}
              </p>
            </div>
          )}
          {refreshNote && (
            <p key={refreshNote.id} role="status" className="zr-note zr-note--good" style={{ margin: '8px 2px 0' }}>
              <Icon name="check" size={16} sw={2.2} />
              <span>{refreshNote.text}</span>
            </p>
          )}
        </div>

        <SheetFoot style={{ marginTop: 'auto' }}>
          <button
            type="button"
            onClick={() => rewards.length > 0 && setPayOpen(true)}
            className="zr-press-soft"
            aria-haspopup={rewards.length > 0 ? 'dialog' : undefined}
            style={{ flex: 'none', width: '100%', height: 48, border: 0, borderTop: '1px solid var(--line)', background: 'transparent', padding: '0 2px', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink)', cursor: rewards.length ? 'pointer' : 'default' }}
          >
            <Icon name="cash" size={20} sw={1.9} />
            <span style={{ fontSize: 15, fontWeight: 700 }}>{t('choose.cash')}</span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left', fontSize: 14, fontWeight: 500, color: off ? 'var(--acText)' : 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {off ? t('choose.rewardOn', { amount: rupees(off) }) : t('choose.cashSub')}
            </span>
            {rewards.length > 0 && <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--acText)' }}>{t('choose.change')}</span>}
          </button>
          <div style={{ paddingTop: 10 }}>
            <Button disabled={!selected} onClick={() => selected && nav({ to: '/pickup', search: tripParams(pickup, drop) })}>
              {selected ? t('choose.book', { type: words(t, selected.vehicleType).label, fare: rupees(selected.farePaise - off) }) : t('choose.bookPlain')}
            </Button>
          </div>
        </SheetFoot>
      </MapSheet>

      {/* cash is the only way to pay until online payments are set up; the sheet is for applying a reward */}
      <Sheet open={payOpen} onClose={() => setPayOpen(false)} title={t('pay.title')}>
        <div style={{ marginTop: 16, height: 60, borderRadius: 18, background: 'var(--acSoft)', boxShadow: 'inset 0 0 0 1.5px var(--sel)', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="cash" size={22} sw={1.9} />
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>{t('pay.cash')}</span>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink2)' }}>{t('pay.cashSub')}</span>
          </span>
          <Icon name="check" size={20} sw={2.6} style={{ color: 'var(--acText)' }} />
        </div>
        {rewards.length > 0 && selected && (
          <div role="radiogroup" aria-label={t('acc.rewards')} style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[null, ...rewards].map((r) => {
              const on = (draft.rewardId ?? null) === (r?.id ?? null);
              const amount = r ? rewardOff(r, selected.farePaise) : 0;
              return (
                <button
                  key={r?.id ?? 'none'}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={!!r && amount <= 0}
                  onClick={() => setDraft({ rewardId: r?.id ?? null })}
                  style={{ minHeight: 52, border: 0, borderRadius: 16, background: on ? 'var(--acSoft)' : 'var(--s1)', boxShadow: on ? 'inset 0 0 0 1.5px var(--sel)' : undefined, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)', textAlign: 'left', cursor: 'pointer' }}
                >
                  <Icon name={r ? 'star' : 'close'} size={18} style={{ color: r ? 'var(--acText)' : 'var(--ink3)' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{r ? t('pay.reward', { title: r.title }) : t('pay.noReward')}</span>
                    {r && <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink2)' }}>{t('pay.rewardOff', { amount: rupees(amount) })}</span>}
                  </span>
                  {on && <Icon name="check" size={18} sw={2.6} style={{ color: 'var(--acText)' }} />}
                </button>
              );
            })}
          </div>
        )}
        <Button onClick={() => setPayOpen(false)} style={{ marginTop: 20 }}>
          {t('common.done')}
        </Button>
      </Sheet>
    </Screen>
  );
}
